import React, { useState } from "react";
import { Form } from "react-crud-form";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Input } from "ui/components/input";
import { Textarea } from "ui/components/textarea";
import { Checkbox } from "ui/components/checkbox";
import { StatusTabInput } from "components/status/tabs/input";
import { DateTimePicker } from "components/datetime-picker";
import { FormActions } from "components/form/actions";
import { FormLabel } from "components/form";
import SelectApplicant from "components/select/applicant";
import { PermissionControlledInput } from "permissions/components/input";
import { interviewSchema } from "./schema";
import { useInterviewForm } from "./useInterviewForm";
import HeaderPortal from "components/portal/header-portal";
import { PermissionGate } from "permissions/components/gate";
import ButtonWithLoading from "@/components/button-with-loading";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "ui/components/dialog";
import { Button } from "ui/components/button";

const InterviewForm = ({ interviewId }) => {
    const { openingId } = useParams();
    const navigate = useNavigate();
    const { form, handleCreate, handleUpdate, handleRead, handleClose, requiredFields } = useInterviewForm({
        openingId,
        interviewId,
    });

    const status = form.watch("status");
    const showPostInterviewFields = status === "interviewed" || status === "closed";

    const [confirmation, setConfirmation] = useState({
        open: false,
        title: "",
        description: "",
        action: null,
    });
    const [actionLoading, setActionLoading] = useState(false);

    const handleConfirm = async () => {
        if (!confirmation.action) return;
        setActionLoading(true);
        try {
            await confirmation.action();
            setConfirmation({ ...confirmation, open: false });
            navigate(-1);
        } catch (error) {
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Form
            form={form}
            create={handleCreate}
            read={handleRead}
            update={handleUpdate}
            itemId={interviewId || "+"}
            schema={interviewSchema}
        >
            {({
                isCreating,
                createItem,
                isNewItem,
                isUpdating,
                updateItem,
                isDirty,
            }) => (
                <div className="space-y-6">
                    <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="opening_interviews"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Status
                                    </FormLabel>
                                    <StatusTabInput
                                        {...field}
                                        collection="opening_interviews"
                                        remove={["closed"]}
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Applicant */}
                    <FormField
                        control={form.control}
                        name="applicant"
                        render={({ field }) => {

                            return (
                                <FormItem>
                                    <PermissionControlledInput
                                        rule={isNewItem ? "create:*" : "update:*"}
                                        collection="opening_interviews"
                                        field={field.name}
                                        type="form"
                                        showWhenDisabled={false}
                                    >
                                        <FormLabel required={requiredFields.includes(field.name)}>
                                            Applicant
                                        </FormLabel>
                                        <SelectApplicant
                                            openingId={openingId}
                                            value={field.value?.id || field.value}
                                            onChange={(selectedId) => {
                                                field.onChange(selectedId ? { id: selectedId } : undefined);
                                            }}
                                            className="w-full"
                                            disabled={!isNewItem}
                                        />
                                    </PermissionControlledInput>
                                    <FormMessage />
                                </FormItem>
                            )
                        }}
                    />

                    {/* Title */}
                    <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="opening_interviews"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>Title</FormLabel>
                                    <Input
                                        type="text"
                                        value={field.value ?? ""}
                                        onChange={(e) =>
                                            field.onChange(
                                                e.target.value === ""
                                                    ? undefined
                                                    : e.target.value
                                            )
                                        }
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Scheduled Date */}
                    <FormField
                        control={form.control}
                        name="date_scheduled"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="opening_interviews"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Schedule At
                                    </FormLabel>
                                    <DateTimePicker {...field} />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {showPostInterviewFields && (
                        <>
                            {/* Recording URL */}
                            <FormField
                                control={form.control}
                                name="recording_url"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create:*" : "update:*"}
                                            collection="opening_interviews"
                                            field={field.name}
                                            type="form"
                                            showWhenDisabled={false}
                                        >
                                            <FormLabel required={requiredFields.includes(field.name)}>
                                                Recording URL
                                            </FormLabel>
                                            <Input
                                                type="text"
                                                value={field.value ?? ""}
                                                onChange={(e) =>
                                                    field.onChange(
                                                        e.target.value === ""
                                                            ? undefined
                                                            : e.target.value
                                                    )
                                                }
                                            />
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Client Feedback */}
                            <FormField
                                control={form.control}
                                name="client_feedback"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create:*" : "update:*"}
                                            collection="opening_interviews"
                                            field={field.name}
                                            type="form"
                                            showWhenDisabled={false}
                                        >
                                            <FormLabel required={requiredFields.includes(field.name)}>Client Feedback</FormLabel>
                                            <Textarea {...field} className="min-h-32" />
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Internal Feedback */}
                            <FormField
                                control={form.control}
                                name="internal_feedback"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create:*" : "update:*"}
                                            collection="opening_interviews"
                                            field={field.name}
                                            type="form"
                                            showWhenDisabled={false}
                                        >
                                            <FormLabel required={requiredFields.includes(field.name)}>Internal Feedback</FormLabel>
                                            <Textarea {...field} className="min-h-32" />
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Transcript */}
                            <FormField
                                control={form.control}
                                name="transcript"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create:*" : "update:*"}
                                            collection="opening_interviews"
                                            field={field.name}
                                            type="form"
                                            showWhenDisabled={false}
                                        >
                                            <FormLabel required={requiredFields.includes(field.name)}>Transcript</FormLabel>
                                            <Textarea {...field} className="min-h-32" />
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Is Selected */}
                            <FormField
                                control={form.control}
                                name="is_cleared"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create: *" : "update:*"}
                                            collection="opening_interviews"
                                            field={field.name}
                                            type="form"
                                        >
                                            <div className="flex items-start gap-2">
                                                <Checkbox
                                                    id="is_cleared"
                                                    checked={Boolean(field.value)}
                                                    onCheckedChange={field.onChange}
                                                />
                                                <div className="grid gap-2">
                                                    <FormLabel required={requiredFields.includes(field.name)}>
                                                        Cleared
                                                    </FormLabel>
                                                    <p className="text-muted-foreground text-sm">
                                                        Mark as cleared if the candidate has cleared this interview.
                                                    </p>
                                                </div>
                                            </div>
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                        </>
                    )}

                    <FormActions
                        isNewItem={isNewItem}
                        isCreating={isCreating}
                        isUpdating={isUpdating}
                        isDirty={isDirty}
                        createItem={() => createItem()}
                        updateItem={() => updateItem().then(() => navigate(-1))}
                        showCancel={false}
                        collection="opening_interviews"
                        rule={isNewItem ? "create:*" : "update:*"}
                    />

                    {!isNewItem && (
                        <HeaderPortal>
                            <PermissionGate
                                rule="update:*"
                                collection="opening_interviews"
                                fallback={
                                    <div className="text-sm text-gray-500 italic">
                                        You don't have permission to update this interview
                                    </div>
                                }
                            >
                                <div className="flex gap-2">
                                    <PermissionControlledInput
                                        rule="update:*"
                                        collection="opening_interviews"
                                    >
                                        <ButtonWithLoading
                                            onClick={() =>
                                                setConfirmation({
                                                    open: true,
                                                    title: "Close this Interview",
                                                    description:
                                                        "This action will close this interview. No new updates can be made. Existing data and history will remain available. This action does not delete any data.",
                                                    action: handleClose,
                                                })
                                            }
                                            disabled={status === "closed" || isUpdating}
                                            variant="outline"
                                        >
                                            {status === "closed" ? "Closed" : "Close Interview"}
                                        </ButtonWithLoading>
                                    </PermissionControlledInput>
                                </div>
                            </PermissionGate>
                        </HeaderPortal>
                    )}

                    <Dialog
                        open={confirmation.open}
                        onOpenChange={(open) =>
                            setConfirmation((prev) => ({ ...prev, open }))
                        }
                    >
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{confirmation.title}</DialogTitle>
                                <DialogDescription className="text-red-500">
                                    {confirmation.description}
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter className="gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        setConfirmation((prev) => ({ ...prev, open: false }))
                                    }
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoading
                                    onClick={handleConfirm}
                                    isLoading={actionLoading}
                                    variant="default"
                                >
                                    Confirm
                                </ButtonWithLoading>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            )}
        </Form>
    );
};

export default InterviewForm;
